import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router';
import { useCallback, useEffect, useState } from 'react';
import * as R from 'Router/routes';
import { Button, Card, Link, Placeholder, Typography } from 'components';
import ChevronIcon from 'assets/icons/chevron-left.svg?react';
import { useClientQuery } from 'hooks/webapi';
import styles from 'pages/Registration/OCRResults/OCRResults.module.scss';
import { useTranslation } from 'react-i18next';
import { format } from 'utils/format';
import { formatAddress } from 'utils/address';

export default function OCRResults() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: client, isLoading } = useClientQuery();

  const [shouldCheckPsd2, setShouldCheckPsd2] = useState(false);

  useEffect(() => {
    if (!shouldCheckPsd2) {
      return;
    }

    navigate(R.PSD2_BANKS);
  }, [setShouldCheckPsd2, shouldCheckPsd2, navigate]);

  const handleClickConfirm = useCallback(async () => {
    setShouldCheckPsd2(true);
  }, []);

  return (
    <>
      <Helmet>
        <title>{t('ocr_results:html_title')}</title>
      </Helmet>

      <div className={styles['box-wrapper']}>
        <Typography
          className={styles.title}
          size="xl"
          kind="h1"
          dataTest="ocrResults-title"
        >
          {t('ocr_results:title')}
        </Typography>
        <Typography className={styles.info} dataTest="ocrResults-info">
          <span
            dangerouslySetInnerHTML={{ __html: t('ocr_results:info') }}
            data-test="ocrResults-info-content"
          />
        </Typography>
      </div>

      <Card className={styles.card}>
        <div className={styles['table-header']}>
          <Typography size="xs" dataTest="ocrResults-fullName">
            {t('ocr_results:fullName')}
          </Typography>
          <Typography
            className={styles['table-title']}
            size="l"
            dataTest="ocrResults-table-title"
          >
            <Placeholder
              loading={isLoading}
              dataTest="ocrResults-table-fullName"
            >
              {client?.fullName}
            </Placeholder>
          </Typography>
        </div>
        <div className={styles.table}>
          <div className={styles.row}>
            <Typography size="xs" dataTest="ocrResults-personal-id">
              {t('ocr_results:personal_id')}
            </Typography>
            <Typography className={styles.value}>
              <Placeholder
                loading={isLoading}
                dataTest="ocrResults-personal-id"
              >
                {client?.personalId}
              </Placeholder>
            </Typography>
          </div>
          <div className={styles.row}>
            <Typography size="xs" dataTest="ocrResults-results-dob">
              {t('ocr_results:dob')}
            </Typography>
            <Typography
              className={styles.value}
              dataTest="ocrResults-dateOfBirth"
            >
              <Placeholder
                loading={isLoading}
                dataTest="ocrResults-dateOfBirth-content"
              >
                {format.dateTime(client?.dateOfBirth, 'date')}
              </Placeholder>
            </Typography>
          </div>
          <div className={styles.row}>
            <Typography size="xs" dataTest="ocrResults-idCard">
              {t('ocr_results:id_card')}
            </Typography>
            <Typography className={styles.value}>
              <Placeholder
                loading={isLoading}
                dataTest="ocrResults-identityDocumentId"
              >
                {client?.identityDocumentId}
              </Placeholder>
            </Typography>
          </div>
          <div className={styles.row}>
            <Typography size="xs" dataTest="ocrResults-address">
              {t('ocr_results:address')}
            </Typography>
            <Typography className={styles.value}>
              <Placeholder
                loading={isLoading}
                dataTest="ocrResults-fullAddress"
              >
                {formatAddress(client?.actualAddress)}
              </Placeholder>
            </Typography>
          </div>
        </div>
      </Card>

      <div className={styles['box-wrapper']}>
        <Button
          className={styles.confirm}
          onClick={handleClickConfirm}
          dataTest="ocrResults-confirm"
        >
          {t('common:confirm')}
        </Button>
        <div className={styles.link}>
          <Link to={R.MANUAL_OCR} dataTest="ocrResults-back-link">
            <ChevronIcon
              className={styles.chevron}
              data-test="ocrResults-chevron-icon"
            />
            {t('common:back')}
          </Link>
        </div>
      </div>
    </>
  );
}
